import { get, omit, some } from 'lodash';
import {
  type ICCAsset,
  type IComponentTag,
  type IProductCategory,
  type IProductVariation,
  type IThemeRes,
  ApiResponse,
  ComponentTypes,
  GcfPriceLabelTypeEnum,
  IComponent,
  IComponentAsset,
  IComponentType,
  IGcfComponentConfig,
  ILayoutType,
} from 'src/interfaces';
import { IPhases } from 'src/interfaces/Phase';
import { generateEnumOptions } from 'src/utils';

export const mapAllComponents = (
  components: ApiResponse[],
  includes?: ApiResponse,
): IComponent[] => {
  return components.map((component: ApiResponse): IComponent => {
    return mapComponent(component, includes);
  });
};

export const mapComponent = (
  component: ApiResponse,
  includes?: ApiResponse,
): IComponent => {
  const layoutTypes = get(includes, 'layoutTypes.data', []) as ILayoutType[];
  const productCategories = get(
    includes,
    'productCategories.data',
    [],
  ) as IProductCategory[];
  const productVariations = get(
    includes,
    'productVariations.data',
    [],
  ) as IProductVariation[];
  const componentTags = get(
    includes,
    'componentTags.data',
    [],
  ) as IComponentTag[];
  const themes = get(includes, 'themes.data', []) as IThemeRes[];
  const assets = get(includes, 'assets.data', []) as ICCAsset[];
  const phases = get(includes, 'phases.data', []) as IPhases[];

  return {
    id: component.id,
    name: component.name,
    layout_type_id: component.layout_type_id,
    layoutType: layoutTypes.find(
      (layoutType) => layoutType.id === component.layout_type_id,
    ),
    phase: phases?.find((phase: IPhases) => phase.id === component.id)?.phase,
    component_type_id: component.component_type_id,
    component_type: mapComponentComponentType(
      get(component, 'component_type_id', -1),
      get(includes, 'componentTypes.data', []),
    ),
    height: component.height,
    width: component.width,
    status: component.status,
    usable_as_override: component.usable_as_override,
    start_date: component.start_date,
    end_date: component.end_date,
    config: mapComponentConfig(
      component.config,
      mapComponentComponentType(
        get(component, 'component_type_id', -1),
        get(includes, 'componentTypes.data', []),
      ),
    ),
    product_category_id: component.product_category_id,
    productCategories: productCategories.filter((item) =>
      component.product_category_id.includes(item.id),
    ),
    product_variation_product_code: component.product_variation_product_code,
    productVariations: productVariations.filter((item) =>
      component.product_variation_product_code.includes(item.product_code),
    ),
    component_tag_id: component.component_tag_id,
    componentTags: componentTags.filter((item) =>
      component.component_tag_id.includes(item.id),
    ),
    theme_id: component.theme_id,
    theme: themes.find((item) => component.theme_id === item.id),
    asset_id: component.asset_id,
    assets: assets.filter((item) => component.asset_id.includes(item.id)),
    created_at: component.created_at,
    modified_at: component.modified_at,
  };
};

export const mapComponentsTypes = (
  componentTypes: ApiResponse[],
): IComponentType[] => {
  return componentTypes.map((componentType: ApiResponse): IComponentType => {
    return mapComponentType(componentType);
  });
};

const mapComponentType = (componentType: ApiResponse): IComponentType => {
  return {
    id: componentType.id,
    name:
      componentType.name === ComponentTypes.PROMOTIONAL_COMPONENT
        ? ComponentTypes.PROMOTIONAL_SPACE
        : componentType.name,
    created_at: componentType.created_at,
    modified_at: componentType.modified_at,
    component_size_range_id: componentType.component_size_range_id,
  };
};

const mapComponentComponentType = (
  componentTypeId: number,
  componentTypes: IComponentType[],
): IComponentType | undefined => {
  const componentType = componentTypes.find(
    (item) => item.id === componentTypeId,
  );
  if (componentType)
    return {
      id: componentType.id,
      name:
        componentType.name === ComponentTypes.PROMOTIONAL_COMPONENT
          ? ComponentTypes.PROMOTIONAL_SPACE
          : componentType.name,
      created_at: componentType.created_at,
      modified_at: componentType.modified_at,
      component_size_range_id: componentType.component_size_range_id,
    };
};

export const mapComponentAssets = (
  componentAssets: ApiResponse[],
): IComponentAsset[] => {
  return componentAssets.map((componentAsset: ApiResponse): IComponentAsset => {
    return mapComponentAsset(componentAsset);
  });
};

const mapComponentAsset = (componentAsset: ApiResponse): IComponentAsset => {
  return {
    id: componentAsset.id,
    component_id: componentAsset.component_id,
    asset_name: componentAsset.asset_name,
    created_at: componentAsset.created_at,
    modified_at: componentAsset.modified_at,
  };
};

const mapComponentConfig = (
  config: Partial<IGcfComponentConfig>,
  componentType?: IComponentType,
) => {
  const isPriceLabelTypeValid = some(
    generateEnumOptions(GcfPriceLabelTypeEnum),
    { value: config?.priceLabel?.type },
  );

  return typeof config === 'string'
    ? JSON.parse(config)
    : config
    ? {
        ...omit(config, 'scaleTypes', 'priceLabel'),
        ...(config.priceLabel &&
          componentType?.id !== 3 && {
            priceLabel: {
              ...config.priceLabel,
              type: isPriceLabelTypeValid
                ? config?.priceLabel?.type
                : GcfPriceLabelTypeEnum.Size,
            },
          }),
      }
    : config;
};
